import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import {
  getOdontogram,
  getOdontogramSnapshot,
  getOdontogramSnapshotByPatientControl,
  getOdontogramSnapshots,
  saveOdontogram,
  type DentitionType,
  type FindingState,
  type Odontogram,
  type OdontogramSnapshotIndex,
  type OdontologyFinding,
  type OdontologyToothStatus,
} from '../../services/OdontologyController';
import { OdontogramGrid, type ArchFilter } from './components/OdontogramGrid';
import { OdontogramChanges } from './components/OdontogramChanges';
import { OdontogramLegend } from './components/OdontogramLegend';
import { OdontogramToolbar } from './components/OdontogramToolbar';
import {
  areTeethInSameArch,
  materializeChart,
  isToothInSpan,
  normalizeOdontogram,
  serializeOdontogram,
  SURFACE_TO_API,
  upsertFinding,
  upsertStatus,
  type Dentition,
  type OdontogramTool,
  type ToothSurface,
} from './odontogram.types';

interface OdontogramViewProps {
  patientId: number;
  initialPatientControlId?: number | null;
  onDirtyChange?: (dirty: boolean) => void;
}

const dateFormatter = new Intl.DateTimeFormat('es-AR', {
  dateStyle: 'short',
  timeStyle: 'short',
});

const getErrorMessage = (error: unknown, fallback: string): string => {
  const candidate = error as { response?: { data?: { message?: string } | string }; message?: string };
  const responseData = candidate.response?.data;
  if (typeof responseData === 'string') return responseData;
  return responseData?.message || candidate.message || fallback;
};

const statusFor = (odontogram: Odontogram, toothId: number): OdontologyToothStatus =>
  odontogram.teeth.find((item) => item.toothFDI === toothId) ?? {
    toothFDI: toothId,
    present: true,
  };

export default function OdontogramView({
  patientId,
  initialPatientControlId,
  onDirtyChange,
}: OdontogramViewProps) {
  const [odontogram, setOdontogram] = useState<Odontogram | null>(null);
  const [snapshots, setSnapshots] = useState<OdontogramSnapshotIndex[]>([]);
  const [selectedTool, setSelectedTool] = useState<OdontogramTool>('caries');
  const [findingState, setFindingState] = useState<FindingState>(1);
  const [icdas, setIcdas] = useState<number | null>(null);
  const [archFilter, setArchFilter] = useState<ArchFilter>('all');
  const [historyCollapsed, setHistoryCollapsed] = useState(false);
  const [selectedTooth, setSelectedTooth] = useState<number | null>(null);
  const [bridgeStart, setBridgeStart] = useState<number | null>(null);
  const [label, setLabel] = useState('Control odontológico');
  const [historicalLabel, setHistoricalLabel] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const initialSnapshot = useRef('');
  const currentRef = useRef<Odontogram | null>(null);

  const chart = useMemo(
    () => materializeChart(odontogram ?? normalizeOdontogram({
      version: 0,
      dentitionType: 3,
      findings: [],
      spans: [],
      teeth: [],
    })),
    [odontogram],
  );
  const currentSerialized = odontogram ? serializeOdontogram(odontogram) : '';
  const readOnly = historicalLabel !== null;
  const dirty = !readOnly && initialSnapshot.current !== '' && initialSnapshot.current !== currentSerialized;
  const selectedToothData = selectedTooth == null ? null : chart[selectedTooth];

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    setSavedMessage(null);
    const currentPromise = getOdontogram(patientId);
    const snapshotsPromise = getOdontogramSnapshots(patientId);
    const historicalPromise = initialPatientControlId
      ? getOdontogramSnapshotByPatientControl(patientId, initialPatientControlId)
      : Promise.resolve(null);

    Promise.all([currentPromise, snapshotsPromise, historicalPromise])
      .then(([currentValue, snapshotValues, historicalValue]) => {
        if (!active) return;
        const normalizedCurrent = normalizeOdontogram(currentValue);
        currentRef.current = normalizedCurrent;
        setSnapshots(snapshotValues);
        if (historicalValue) {
          const normalizedHistorical = normalizeOdontogram(historicalValue);
          setOdontogram(normalizedHistorical);
          setHistoricalLabel('Versión histórica');
          initialSnapshot.current = serializeOdontogram(normalizedHistorical);
        } else {
          setOdontogram(normalizedCurrent);
          setHistoricalLabel(null);
          initialSnapshot.current = serializeOdontogram(normalizedCurrent);
        }
        onDirtyChange?.(false);
      })
      .catch((requestError) => {
        if (active) setError(getErrorMessage(requestError, 'No se pudo cargar el módulo odontológico.'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      onDirtyChange?.(false);
    };
  }, [initialPatientControlId, onDirtyChange, patientId]);

  const updateOdontogram = useCallback((updater: (current: Odontogram) => Odontogram) => {
    if (readOnly) return;
    setSavedMessage(null);
    setOdontogram((current) => current ? updater(current) : current);
  }, [readOnly]);

  const applyTool = useCallback((toothId: number, surface: ToothSurface) => {
    setSelectedTooth(toothId);
    setError(null);
    if (readOnly || !odontogram) return;
    const appliedFindingState: FindingState = selectedTool === 'caries'
      ? 1
      : selectedTool === 'restoration'
        ? 2
        : findingState;

    if (selectedTool === 'bridge') {
      if (bridgeStart == null) {
        setBridgeStart(toothId);
        return;
      }
      if (!areTeethInSameArch(bridgeStart, toothId)) {
        setError('El puente debe registrarse entre piezas del mismo arco y dentición.');
        setBridgeStart(null);
        return;
      }
      updateOdontogram((current) => ({
        ...current,
        spans: [
          ...current.spans.filter((span) => span.typeCode !== 'bridge'
            || span.fromToothFDI !== bridgeStart || span.toToothFDI !== toothId),
          {
            fromToothFDI: bridgeStart,
            toToothFDI: toothId,
            typeCode: 'bridge',
            state: appliedFindingState,
          },
        ],
      }));
      setBridgeStart(null);
      return;
    }

    updateOdontogram((current) => {
      if (selectedTool === 'clearTooth') {
        return {
          ...current,
          findings: current.findings.filter((finding) => finding.toothFDI !== toothId),
          spans: current.spans.filter((span) => !isToothInSpan(toothId, span)),
          teeth: current.teeth.filter((status) => status.toothFDI !== toothId),
        };
      }
      if (selectedTool === 'absent') {
        const previous = statusFor(current, toothId);
        return {
          ...current,
          teeth: upsertStatus(current.teeth, { ...previous, present: false }),
        };
      }
      if (selectedTool === 'healthy') {
        const apiSurface = SURFACE_TO_API[surface];
        return {
          ...current,
          findings: current.findings.filter((finding) =>
            !(finding.toothFDI === toothId && finding.surface === apiSurface)),
        };
      }

      const wholeTooth = selectedTool === 'crown' || selectedTool === 'endodontics';
      const nextFinding: OdontologyFinding = {
        toothFDI: toothId,
        typeCode: selectedTool,
        state: appliedFindingState,
        ...(wholeTooth ? {} : { surface: SURFACE_TO_API[surface] }),
        ...(selectedTool === 'caries' && icdas != null ? { icdas } : {}),
      };
      const withoutCompetingSurfaceFinding = wholeTooth
        ? current.findings
        : current.findings.filter((finding) =>
          !(finding.toothFDI === toothId && finding.surface === nextFinding.surface));
      return {
        ...current,
        findings: upsertFinding(withoutCompetingSurfaceFinding, nextFinding),
      };
    });
  }, [
    bridgeStart,
    findingState,
    icdas,
    odontogram,
    readOnly,
    selectedTool,
    updateOdontogram,
  ]);

  const updateToothStatus = useCallback((changes: Partial<OdontologyToothStatus>) => {
    if (selectedTooth == null) return;
    updateOdontogram((current) => {
      const previous = statusFor(current, selectedTooth);
      return {
        ...current,
        teeth: upsertStatus(current.teeth, { ...previous, ...changes, toothFDI: selectedTooth }),
      };
    });
  }, [selectedTooth, updateOdontogram]);

  const changeDentitionType = useCallback((dentitionType: DentitionType) => {
    updateOdontogram((current) => ({ ...current, dentitionType }));
  }, [updateOdontogram]);

  const handleSave = useCallback(async () => {
    if (!odontogram || readOnly) return;
    setSaving(true);
    setError(null);
    setSavedMessage(null);
    try {
      const saved = normalizeOdontogram(await saveOdontogram(patientId, {
        ...odontogram,
        label: label.trim() || undefined,
      }));
      setOdontogram(saved);
      currentRef.current = saved;
      initialSnapshot.current = serializeOdontogram(saved);
      setSnapshots(await getOdontogramSnapshots(patientId));
      onDirtyChange?.(false);
      setSavedMessage('Control odontológico guardado en la historia clínica.');
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'No se pudo guardar el odontograma.'));
    } finally {
      setSaving(false);
    }
  }, [label, odontogram, onDirtyChange, patientId, readOnly]);

  const showSnapshot = useCallback(async (snapshot: OdontogramSnapshotIndex) => {
    setLoading(true);
    setError(null);
    try {
      const value = normalizeOdontogram(await getOdontogramSnapshot(patientId, snapshot.id));
      setOdontogram(value);
      setHistoricalLabel(`${snapshot.label || 'Control odontológico'} · ${dateFormatter.format(new Date(snapshot.date))}`);
      initialSnapshot.current = serializeOdontogram(value);
      onDirtyChange?.(false);
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'No se pudo abrir la versión histórica.'));
    } finally {
      setLoading(false);
    }
  }, [onDirtyChange, patientId]);

  const showCurrent = useCallback(() => {
    if (!currentRef.current) return;
    setOdontogram(currentRef.current);
    setHistoricalLabel(null);
    initialSnapshot.current = serializeOdontogram(currentRef.current);
    setError(null);
    onDirtyChange?.(false);
  }, [onDirtyChange]);

  if (loading || !odontogram) {
    return (
      <div className="flex min-h-[420px] flex-col items-center justify-center gap-3" role="status">
        <ProgressSpinner style={{ width: '48px', height: '48px' }} />
        <span className="text-sm text-slate-500">Cargando historia odontológica…</span>
      </div>
    );
  }

  const dentitions: Dentition[] = odontogram.dentitionType === 3
    ? ['permanent', 'temporary']
    : [odontogram.dentitionType === 1 ? 'permanent' : 'temporary'];

  return (
    <div className="min-h-full bg-slate-50 p-3 sm:p-5">
      <div className={`mx-auto grid max-w-[1380px] gap-4 transition-[grid-template-columns] ${
        historyCollapsed
          ? 'xl:grid-cols-[minmax(0,1fr)_48px]'
          : 'xl:grid-cols-[minmax(0,1fr)_280px]'
      }`}>
        <main className="flex min-w-0 flex-col gap-3">
          <header>
            <h1 className="text-lg font-bold text-slate-900">Odontología</h1>
            <p className="text-xs text-slate-500">
              Odontograma relacional · ISO 3950 FDI · control de versiones
            </p>
          </header>

          {historicalLabel ? (
            <Message severity="info" text={`Modo histórico: ${historicalLabel}`} className="w-full" />
          ) : null}
          {error ? <Message severity="error" text={error} className="w-full" /> : null}
          {savedMessage ? <Message severity="success" text={savedMessage} className="w-full" /> : null}

          <OdontogramToolbar
            selectedTool={selectedTool}
            onToolChange={(tool) => {
              setSelectedTool(tool);
              if (tool === 'caries') setFindingState(1);
              if (tool === 'restoration') setFindingState(2);
              if (tool !== 'bridge') setBridgeStart(null);
            }}
            findingState={findingState}
            onFindingStateChange={setFindingState}
            icdas={icdas}
            onIcdasChange={setIcdas}
            bridgeStart={bridgeStart}
            dentitionType={odontogram.dentitionType}
            onDentitionTypeChange={changeDentitionType}
            archFilter={archFilter}
            onArchFilterChange={(v) => setArchFilter(v as 'all' | 'upper' | 'lower')}
            readOnly={readOnly}
          />

          {dentitions.map((dentition) => (
            <OdontogramGrid
              key={dentition}
              chart={chart}
              dentition={dentition}
              archFilter={archFilter}
              selectedTool={selectedTool}
              onApply={applyTool}
              onSelect={setSelectedTooth}
            />
          ))}

          {!readOnly && currentRef.current ? (
            <OdontogramChanges before={currentRef.current} after={odontogram} />
          ) : null}

          {selectedToothData ? (
            <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-bold text-slate-800">Pieza {selectedToothData.toothId}</h2>
                  <p className="text-xs text-slate-500">Estado periodontal y observaciones de la pieza</p>
                </div>
                <span className={`rounded-full px-2 py-1 text-xs font-semibold ${
                  selectedToothData.absent ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'
                }`}>
                  {selectedToothData.absent ? 'Ausente' : 'Presente'}
                </span>
              </div>
              <div className="grid gap-3 md:grid-cols-[180px_180px_minmax(220px,1fr)]">
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-semibold text-slate-600">Movilidad (Miller)</label>
                  <Dropdown
                    value={selectedToothData.mobility ?? null}
                    disabled={readOnly}
                    onChange={(e) => updateToothStatus({ mobility: e.value ?? undefined })}
                    options={[
                      { label: 'Sin registrar', value: null },
                      { label: 'Grado 0', value: 0 },
                      { label: 'Grado 1', value: 1 },
                      { label: 'Grado 2', value: 2 },
                      { label: 'Grado 3', value: 3 },
                    ]}
                    className="p-inputtext-sm"
                    placeholder="Sin registrar"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-semibold text-slate-600">Furcación (Glickman)</label>
                  <Dropdown
                    value={selectedToothData.furcation ?? null}
                    disabled={readOnly}
                    onChange={(e) => updateToothStatus({ furcation: e.value ?? undefined })}
                    options={[
                      { label: 'Sin registrar', value: null },
                      { label: 'Grado 0', value: 0 },
                      { label: 'Grado 1', value: 1 },
                      { label: 'Grado 2', value: 2 },
                      { label: 'Grado 3', value: 3 },
                      { label: 'Grado 4', value: 4 },
                    ]}
                    className="p-inputtext-sm"
                    placeholder="Sin registrar"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-semibold text-slate-600">Observaciones</label>
                  <InputTextarea value={selectedToothData.notes} disabled={readOnly} maxLength={1000}
                    onChange={(event) => updateToothStatus({ notes: event.target.value || undefined })}
                    rows={2} className="p-inputtext-sm" style={{ width: '100%' }} />
                </div>
              </div>
            </section>
          ) : null}

          {!readOnly ? (
            <label className="flex flex-col gap-1 rounded-xl border border-slate-200 bg-white p-3 text-xs font-semibold text-slate-600">
              Etiqueta del control
              <input value={label} maxLength={80} onChange={(event) => setLabel(event.target.value)}
                className="rounded-md border border-slate-300 px-3 py-2 text-sm font-normal" />
            </label>
          ) : null}
          <OdontogramLegend />

          {!readOnly ? (
            <div className="sticky bottom-0 z-10 flex justify-end px-3 py-3">
              <Button
                icon="pi pi-check"
                label={dirty ? 'Guardar odontograma' : 'Guardado'}
                onClick={handleSave}
                loading={saving}
                disabled={saving || !dirty}
                className="min-h-[38px] rounded-lg !border-[#3CB8BA] !bg-[#3CB8BA] text-sm font-semibold !text-white hover:!border-[#01717C] hover:!bg-[#01717C]"
              />
            </div>
          ) : null}
        </main>

        <aside className={`rounded-xl border border-slate-200 bg-white shadow-sm xl:sticky xl:top-3 xl:max-h-[calc(100vh-2rem)] ${
          historyCollapsed ? 'p-1' : 'p-3 xl:overflow-y-auto'
        }`}>
          {historyCollapsed ? (
            <button
              type="button"
              aria-expanded="false"
              title="Mostrar historial"
              onClick={() => setHistoryCollapsed(false)}
              className="flex min-h-11 w-full items-center justify-center gap-2 rounded-lg text-cyan-700 transition hover:bg-cyan-50 xl:min-h-[160px] xl:flex-col"
            >
              <i className="pi pi-angle-left" aria-hidden="true" />
              <span className="text-xs font-bold xl:[writing-mode:vertical-rl] xl:rotate-180">
                Historial ({snapshots.length})
              </span>
            </button>
          ) : (
            <>
              <div className="mb-3 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <h2 className="text-sm font-bold text-slate-800">Historial</h2>
                  <p className="text-xs text-slate-500">{snapshots.length} controles guardados</p>
                </div>
                <div className="flex shrink-0 items-center">
                  {readOnly ? <Button label="Vigente" icon="pi pi-refresh" text onClick={showCurrent} /> : null}
                  <button
                    type="button"
                    aria-expanded="true"
                    aria-label="Ocultar historial"
                    title="Ocultar historial"
                    onClick={() => setHistoryCollapsed(true)}
                    className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition hover:bg-cyan-50 hover:text-cyan-700"
                  >
                    <i className="pi pi-angle-right" aria-hidden="true" />
                  </button>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                {snapshots.map((snapshot) => (
                  <button key={snapshot.id} type="button" onClick={() => showSnapshot(snapshot)}
                    className="rounded-lg border border-cyan-200 bg-cyan-50 p-3 text-left transition hover:border-cyan-400 hover:bg-cyan-50">
                    <span className="block text-xs font-bold text-slate-700">
                      {snapshot.label || 'Control odontológico'}
                    </span>
                    <span className="mt-1 block text-xs text-slate-500">
                      {dateFormatter.format(new Date(snapshot.date))}
                    </span>
                    <span className="mt-1 block text-[11px] text-slate-400">
                      {snapshot.findingCount} hallazgos · PC #{snapshot.patientControlId}
                    </span>
                  </button>
                ))}
                {snapshots.length === 0 ? (
                  <p className="rounded-lg bg-slate-50 p-3 text-xs text-slate-500">
                    Todavía no hay controles odontológicos guardados.
                  </p>
                ) : null}
              </div>
            </>
          )}
        </aside>
      </div>
    </div>
  );
}
